"use client";

import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const faqs = [{
  question: "What is your shelf-life testing protocol?",
  answer: "We perform comprehensive stability testing in our FDA-registered facility, including both accelerated and real-time protocols. This ensures that functional ingredients like probiotics, adaptogens, and vitamins maintain their potency and the carbonation remains crisp throughout the product's lifecycle."
}, {
  question: "Do you support 12oz sleek or standard cans?",
  answer: "Our high-speed counter-pressure canning lines are optimized for the industry-standard 12oz sleek aluminum cans preferred by wellness brands. However, our equipment is fully adjustable to accommodate standard 12oz, 16oz, and specialty slim formats based on your project requirements."
}, {
  question: "What are the minimum order quantities (MOQ)?",
  answer: "We specialize in supporting growing brands with a flexible low-MOQ model. Custom formulation runs typically begin at 5,000 units, allowing you to test the market with premium nutraceutical fizzes or electrolyte drinks without the burden of massive inventory overhead."
}, {
  question: "How do you handle carbonation and nitrogen dosing?",
  answer: "Our facility utilizes advanced counter-pressure technology for precise CO2 volumes, essential for the delicate mouthfeel of seltzers. We also offer liquid nitrogen dosing for non-carbonated functional beverages to provide structural integrity to the sleek aluminum cans and preserve ingredient freshness."
}];
export default function QuoteProcessFaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="90075" id="common-inquiries" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div ref={ref} className={`mx-auto max-w-4xl rounded-2xl bg-card/40 backdrop-blur-sm border border-border/40 p-6 sm:p-10 md:p-14 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-bold tracking-tighter text-foreground sm:text-4xl mb-4 font-sans">
              Common Inquiries
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-sans">
              Technical details regarding our FDA-registered manufacturing process, canning specifications, and custom formulation capabilities.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border/40 py-2">
                <AccordionTrigger className="text-left text-base sm:text-lg font-semibold hover:no-underline hover:text-primary transition-colors [&>svg]:text-primary [&>svg]:size-5 font-sans">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed pt-2 pb-6 font-sans">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}